import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhotoFilm } from '@fortawesome/pro-light-svg-icons/faPhotoFilm';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelHead } from '../../components/primitives/Panel';
import { SerializedSocialChannel } from '../types';
import { ComposerAiNotice } from './ComposerAiNotice';
import { ComposerAttachedMedia } from './ComposerAttachedMedia';
import { ComposerFormatSection } from './ComposerFormatSection';
import { ComposerDraft } from './useComposerDraft';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import styles from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ComposerMediaPanelProps {
  accountId: number;
  /** The channels of the artist: the AI notice says when one of the picked channels is posted by hand. */
  channels: SerializedSocialChannel[];
  draft: ComposerDraft;
  onOpenMediaPicker: () => void;
}

/**
 * The first step of the Composer (owner request 2026-10-09): the media of the post and its
 * format. The media decides which channels can take the post, and a kit asset of a song picks
 * the release.
 */
const ComposerMediaPanelComponent: React.FC<ComposerMediaPanelProps> = ({
  accountId,
  channels,
  draft,
  onOpenMediaPicker,
}) => {
  const intl = useIntl();

  return (
    <Panel data-testid="composer-media-panel">
      <PanelHead
        icon={<FontAwesomeIcon icon={faPhotoFilm} />}
        title={intl.formatMessage({ id: 'app.sml.promote.composer.attachedMedia', defaultMessage: 'Attached media' })}
        subtitle={intl.formatMessage({
          id: 'app.sml.promote.composer.media.hint',
          defaultMessage: 'Start with the media. It decides which channels can take the post.',
        })}
      />
      <div className={styles.copyBody}>
        <ComposerAttachedMedia
          media={draft.media}
          mediaSong={draft.mediaSong}
          releaseTitle={draft.release?.title}
          onLinkToMediaSong={draft.linkToMediaSong}
          onOpenPicker={onOpenMediaPicker}
          onDetach={draft.detachMedia}
        />
        <ComposerAiNotice accountId={accountId} media={draft.media} channels={channels} platforms={draft.platforms} />
      </div>
      <div className={styles.copySection}>
        <ComposerFormatSection
          format={draft.format}
          hasMedia={draft.media !== undefined}
          onChangeFormat={draft.changeFormat}
        />
      </div>
    </Panel>
  );
};

export const ComposerMediaPanel = TypedMemo(ComposerMediaPanelComponent);
